import React, { useState, useEffect } from "react"
import styled from "styled-components"
import { useForm, Controller } from "react-hook-form"
import InputMask from "react-input-mask"
import { ErrorMessage } from "@hookform/error-message"
import Picker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Row, Column } from "../components/layout"
import Image from "../components/image"
import { encode, track } from "../utils"
import { isFuture, isSaturday, isSunday, format } from "date-fns"
import Heart from "../icons/heart.inline.svg"

export default styled(({ className, data }) => {
  const [status, setStatus] = useState("initial")
  const [trackedBegun, setTrackedBegun] = useState(false)
  const { register, handleSubmit, formState, getValues, watch, errors, control } = useForm({
    mode: "onChange",
  })
  const watchDate = watch("Date")
  const { isDirty } = formState
  const onSubmit = ({ Date: date, ...values }) => {
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "schedule-consultation",
        Date: format(new Date(date), "MM/dd/yyyy"),
        ...values,
      }),
    })
      .then(() => {
        setStatus("success")
        track({ type: "lead" })
        fetch("/.netlify/functions/salesforce", {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({ Date: format(new Date(date), "yyyy-MM-dd"), ...values }),
        })
      })
      .catch((error) => {
        setStatus("failed")
        console.error("Fetch Error: ", error)
        console.log("Form Field errors: ", errors)
      })
  }

  useEffect(() => {
    if (isDirty && !trackedBegun) {
      track({ type: "begin_form" })
      setTrackedBegun(true)
    }
  }, [isDirty, trackedBegun])

  if (status === "success") {
    return (
      <Row id="primary-form" className={className} justify="center" align="center">
        <Heart className="icon" />
        <h2 className="cta">Your Request has Been Received!</h2>
        <hr />
        <div className="status">
          Thank you for requesting a consultation. Our team will process your request and reach out as soon as possible
          with more details.
        </div>
      </Row>
    )
  }

  if (status === "failed") {
    return (
      <Row id="primary-form" className={className} justify="center" align="center">
        <h2 className="cta">Form Submission Failed!</h2>
        <hr />
        <div className="status">Please contact an administrator.</div>
      </Row>
    )
  }

  console.log(data)

  return (
    <Row id="primary-form" className={className} justify="center" align="center">
      <Image 
        className="badge"
        src={data.badge} 
        style={{marginTop:"-4rem"}}
        alt="Star Tribune Readers' Choice 2021 Gold Winner - Minnesota's Best Weight Loss Center"
      />
      <h2 className="cta color-red">Extended Offer!<br/><strong><span className="larger">50%</span> Off<br/>Black Friday</strong></h2>
      <hr />
      <form
        data-netlify="true"
        name="schedule-consultation"
        netlify-honeypot="bot-field"
        onSubmit={handleSubmit(onSubmit)}
      >
        <label style={{ display: "none" }}>
          Don’t fill this out if you're human: <input name="bot-field" />
        </label>
        <div className="row">
          <Column>
            <input
              aria-invalid={errors.Last && true}
              type="text"
              name="First"
              ref={register({ required: true, maxLength: 25 })}
            />
            <label className="label" htmlFor="First">
              First
            </label>
          </Column>
          <Column>
            <input
              aria-invalid={errors.Last && true}
              type="text"
              name="Last"
              ref={register({ required: true, maxLength: 50 })}
            />
            <label htmlFor="Last">Last</label>
          </Column>
        </div>
        <div className="row">
          <Column>
            <input
              aria-invalid={errors.Email && true}
              type="text"
              name="Email"
              ref={register({ required: true, pattern: /^\S+@\S+$/i })}
            />
            <label htmlFor="Email">Email</label>
          </Column>
          <Column>
            <InputMask
              mask={getValues("Phone") !== "" ? "(999) 999-9999" : null}
              aria-invalid={errors.Phone && true}
              type="tel"
              autoComplete="tel"
              name="Phone"
              ref={register({ required: true, maxLength: 14 })}
            />
            <label htmlFor="Phone">Phone</label>
          </Column>
        </div>
        <div className="row date-time">
          <Column className="appt-col">
            <label htmlFor="Date" aria-invalid={errors.Date && true}>
              Requested Date for Appointment
            </label>
            <ErrorMessage
              errors={errors}
              name="Date"              
              render={({ message }) => (
                <div
                  className="requested-date-error"
                  style={{
                    color: "#E53E51",
                    lineHeight: 2,
                    fontSize: "14px",
                  }}
                >
                  {message}
                </div>
              )}
            />
            <Controller
              control={control}
              aria-invalid={errors.Date && true}
              rules={{
                required: "This field is required.",
              }}
              name="Date"
              render={({ value, ...props }) => (
                <Picker
                  selected={value}
                  {...props}
                  placeholderText="mm/dd/yyyy"
                  filterDate={(v) => isFuture(v) && !isSunday(v)}
                  aria-invalid={errors.Date && true}
                  popperPlacement="bottom-end"
                  showPopperArrow={false}
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: false,
                      boundariesElement: "viewport",
                    },
                  }}
                />
              )}
            />
            <sub style={{ lineHeight: 2 }}>Appointments available Monday - Saturday</sub>
          </Column>
          <Column style={{ display: watchDate ? "block" : "none" }}>
            <label htmlFor="Time">Requested Time for Appointment</label>
            <select name="Time" ref={register({ required: true })}>
              <option value="9:00am to 10:00am">9 AM to 10 AM</option>
              <option value="10:00am to 11:00am">10 AM to 11 AM</option>
              <option value="11:00am to 12:00pm">11 AM to 12 PM</option>
              {!isSaturday(watchDate) && (
                <>
                  <option value="12:00pm to 1:00pm">12 PM to 1 PM</option>
                  <option value="1:00pm to 2:00pm">1 PM to 2 PM</option>
                  <option value="2:00pm to 3:00pm">2 PM to 3 PM</option>
                  <option value="3:00pm to 4:00pm">3 PM to 4 PM</option>
                  <option value="4:00pm to 5:00pm">4 PM to 5 PM</option>
                  <option value="5:00pm to 6:00pm">5 PM to 6 PM</option>
                  <option value="6:00pm to 7:00pm">6 PM to 7 PM</option>
                </>
              )}
            </select>
          </Column>
        </div>
        <div className="row">
          <Column className="appt-col">
            <label aria-invalid={errors["Preferred Location"] && true} htmlFor="Preferred Location">Preferred Location</label>
            <ErrorMessage
              errors={errors}
              name="Preferred Location"              
              render={({ message }) => (
                <div
                  className="preferred-location-error"
                  style={{
                    color: "#E53E51",
                    lineHeight: 2,
                    fontSize: "14px",
                  }}
                >
                  {message}
                </div>
              )}
            />
            <select 
              name="Preferred Location" 
              ref={register({ required: "Please select a location." })}
              aria-invalid={errors["Preferred Location"] && true}
            >
              <option value="" selected disabled hidden>Please select...</option>
              <option value="Apple Valley">Apple Valley</option>
              <option value="Coon Rapids">Coon Rapids</option>
              <option value="Minnetonka">Minnetonka</option>
              <option value="Roseville">Roseville</option>
              <option value="St. Cloud">St. Cloud</option>
              <option value="Woodbury">Woodbury</option>
              <option value="Rochester">Rochester</option>
              <option value="Eau Claire">Eau Claire</option>
              <option value="Mankato">Mankato</option>
              <option value="Edina">Edina</option>
            </select>
            
          </Column>
        </div>
        <Row>
          <input type="submit" value="Schedule Now" />
          <p className="fine-print">
            *Offer not redeemable for cash and has no cash value. Valid for new Livea clients starting a new weight loss
            program. See Center for details. Typical weight loss is 2-5 lbs. per week for the first two weeks, then 1-2
            lbs. per week thereafter. Average weight loss for Livea Weight Control Centers clients is 25 pounds.
          </p>
        </Row>
      </form>
    </Row>
  )
})`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 650px;
  align-items: center;
  background: white;
  font-family: ${({ theme }) => theme.body};
  padding: 1.5rem;
  box-shadow: 0px 3px 6px #00000029;
  transition: all 500ms ease-in;

  form {
    width: 100%;
    margin-top:.75rem;
  }

  .status {
    text-align: center;
  }

  .icon {
    width: 6rem;
    height: 6rem;
  }

  .color-mint {
    color: ${({ theme }) => theme.mint};
  }

  .color-red {
    color: ${({ theme }) => theme.red};
  }

  .row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    flex-flow: row;
    width: 100%;
  }

  .row.date-time {
    flex-flow: column;
    width: 100%;
  }

  ${Column} {
    display: flex;
    flex: 1 1 100%;
    width: 100%;
    padding: 0 0.5rem;
    flex-flow: column-reverse;
  }

  .appt-col {
    flex-flow: column;
    position: relative;
  }

  h2 {
    font-family: ${({ theme }) => theme.heading};
    color: ${({ theme }) => theme.gray};
    text-align: center;
    position: relative;
    line-height: 1.875rem;
    margin: 0.5rem auto;
  }

  hr {
    background: ${({ theme }) => theme.red};
    width: 12.5rem;
    height: 3px;
    border: 0;
  }

  .subhead {
    font-size: 1.4rem;
    margin-top: 0.5rem;
    max-width: 20rem;
    text-align: center;
  }

  .badge {
    margin:.25rem auto .75rem;
  }

  .cta strong {
    display:inline-block;
    break-inside:avoid;
  }

  .larger {
    font-size:2.0rem;
  }

  .fine-print {
    font-size: 0.75rem;
    text-align: center;
    color: #767676;
  }

  label {
    display: block;
    width: 100%;
    font-size: 0.875rem;
    line-height: 1.75rem;
    font-family: ${({ theme }) => theme.body};
  }

  input {
    padding: 0.5rem 0.75rem;
    border: 1px solid ${({ theme }) => theme.darkGray};
    border-radius: 0;
    width: 100%;
    height: 3rem;
    background: white;
  }

  select {
    display: block;
    background: white;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.darkGray};
    border-radius: 0;
    padding: 0.5rem 1rem;
    height: 3rem;
  }

  input[type="submit"] {
    background: ${({ theme }) => theme.red};
    margin: 1.5rem auto 0.5rem;
    border-radius: 1.5rem;
    font-size: 1rem;
    font-weight: 700;
    border: 0;
    color: white;
    line-height: 1.5rem;
    width: 14.25rem;
    cursor: pointer;
    transition: all 300ms ease;
    &:hover,
    &:active {
      background-color: #f26722;
    }
  }

  input[aria-invalid="true"],
  select[aria-invalid="true"] {
    border: 2px solid ${({ theme }) => theme.red};
  }

  label[aria-invalid="true"] {
    font-weight: 700;
    color: ${({ theme }) => theme.red};
  }

  label[aria-invalid="true"] ~ .react-datepicker-wrapper input {
    border: 2px solid ${({ theme }) => theme.red};
  }

  input[aria-invalid="true"] + label::after {
    content: "This field is required.";
    float: right;
    font-weight: normal;
  }

  input[aria-invalid="true"] + label,
  select[aria-invalid="true"] + label {
    color: ${({ theme }) => theme.red};
    font-weight: 700;
  }

  @media (min-width: ${({ theme }) => theme.md}) {
    min-width: 500px;
    padding: 1.625rem;
    .row {
      flex-flow: row;
    }

    .requested-date-error,
    .preferred-location-error {
      position:absolute;
      right:5px;
      top:0;
      text-align:right;
    }

    ${Column} {
      flex: 1 0 50%;
      padding: 0 0.5rem;
    }
  }
`
